import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  ExpressCheckoutElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import { deviceApi } from '../../services/api';
import SpinningSun from '../../components/common/SpinningSun';
import './Checkout.css';

// Size options with three tiers plus custom option
const SIZE_OPTIONS = [
  { id: 'small', amount: 3, price: 1.00, label: '$1', description: '🤏 Half Body', default: false },
  { id: 'medium', amount: 6, price: 2.00, label: '$2', description: '🧍 Full Body', default: true },
  { id: 'large', amount: 9, price: 4.00, label: '$4', description: '👫 Two Bodys', default: false },
  { id: 'custom', amount: 0, price: 0, label: 'Custom', description: '🎉 Buy the block a round', default: false }
];

// Status messages
const STATUS_MESSAGES = {
  busy: "Another customer is using the Block. Try again in 1 minute.",
  inactive: "Block is out of operating hours.",
  offline: "Block is currently offline. Please try another location.",
  error: "Something went wrong. Please try again."
};

// Google Pay Button Component following official brand guidelines
function GooglePayButton({ onGooglePayClick, isProcessing, amount, stripePublishableKey }) {
  const googlePayButtonRef = useRef(null);
  const [isGooglePayReady, setIsGooglePayReady] = useState(false);
  const [paymentsClient, setPaymentsClient] = useState(null);

  useEffect(() => {
    if (!stripePublishableKey || !amount) {
      console.log('Google Pay: Waiting for Stripe key and amount...');
      return;
    }
    
    if (!amount) {
      console.log('Google Pay: Waiting for amount...');
      return;
    }

    // Load Google Pay API
    const loadGooglePayAPI = async () => {
      if (window.google && window.google.payments) {
        await initializeGooglePay();
      } else {
        const script = document.createElement('script');
        script.src = 'https://pay.google.com/gp/p/js/pay.js';
        script.onload = () => initializeGooglePay();
        script.onerror = () => console.error('Failed to load Google Pay API');
        document.head.appendChild(script);
      }
    };

    const initializeGooglePay = async () => {
      try {
        const baseRequest = {
          apiVersion: 2,
          apiVersionMinor: 0
        };

        // Configure tokenization for Stripe integration
        const tokenizationSpecification = {
          type: 'PAYMENT_GATEWAY',
          parameters: {
            gateway: 'stripe',
            'stripe:version': '2018-10-31',
            'stripe:publishableKey': stripePublishableKey
          }
        };

        const allowedPaymentMethods = [{
          type: 'CARD',
          parameters: {
            allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
            allowedCardNetworks: ['AMEX', 'DISCOVER', 'MASTERCARD', 'VISA']
          },
          tokenizationSpecification
        }];

        const client = new window.google.payments.api.PaymentsClient({
          environment: process.env.NODE_ENV === 'production' ? 'PRODUCTION' : 'TEST'
        });

        setPaymentsClient(client);

        // Check if Google Pay is available
        const response = await client.isReadyToPay({
          ...baseRequest,
          allowedPaymentMethods
        });
        
        if (response.result) {
          setIsGooglePayReady(true);
          createGooglePayButton(client, baseRequest, allowedPaymentMethods);
          console.log('✅ Google Pay is ready!');
        } else {
          console.log('❌ Google Pay not available on this device/browser');
        }
      } catch (err) {
        console.error('Google Pay initialization error:', err);
      }
    };

    const createGooglePayButton = (client, baseRequest, allowedPaymentMethods) => {
      if (googlePayButtonRef.current) {
        try {
          // Use official Google Pay createButton method for brand compliance
          const button = client.createButton({
            onClick: () => handleGooglePayClick(client, baseRequest, allowedPaymentMethods),
            buttonType: 'pay', // Use 'pay' for personalization features
            buttonSizeMode: 'static',
            buttonColor: 'black' // Use dark button for light backgrounds as per guidelines
          });
          
          // Clear any existing button and append new one
          googlePayButtonRef.current.innerHTML = '';
          googlePayButtonRef.current.appendChild(button);
        } catch (err) {
          console.error('Error creating Google Pay button:', err);
        }
      }
    };

    const handleGooglePayClick = async (client, baseRequest, allowedPaymentMethods) => {
      if (isProcessing) {
        console.log('Google Pay: Already processing, ignoring click');
        return;
      }

      try {
        const paymentDataRequest = {
          ...baseRequest,
          allowedPaymentMethods,
          transactionInfo: {
            totalPriceStatus: 'FINAL',
            totalPrice: (amount / 100).toFixed(2),
            currencyCode: 'USD',
            countryCode: 'US'
          },
          merchantInfo: {
            merchantName: 'Block Sunscreen'
            // merchantId is optional for testing, required for production
          }
        };

        console.log('Google Pay: Requesting payment data...', paymentDataRequest);
        const paymentData = await client.loadPaymentData(paymentDataRequest);
        console.log('Google Pay: Payment data received', paymentData);
        
        // Call the parent handler for Stripe integration
        onGooglePayClick(paymentData);
      } catch (err) {
        if (err.statusCode === 'CANCELED') {
          console.log('Google Pay: User canceled payment');
        } else {
          console.error('Google Pay: Payment error:', err);
        }
      }
    };

    loadGooglePayAPI();
  }, [amount, isProcessing, onGooglePayClick, stripePublishableKey]);

  // Don't render anything if Google Pay is not ready
  if (!isGooglePayReady) {
    return (
      <div className="google-pay-loading" style={{ 
        height: '48px', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        color: '#666',
        fontSize: '14px'
      }}>
        Loading Google Pay...
      </div>
    );
  }

  return (
    <div className="google-pay-container" style={{
      margin: '8px 0', // Maintain 8dp clear space as per Google Pay guidelines
      minHeight: '48px'
    }}>
      <div 
        ref={googlePayButtonRef} 
        className="google-pay-button"
        style={{
          minHeight: '48px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      />
    </div>
  );
}

// Inner form component that uses Stripe hooks
function PaymentForm({ deviceSerial, selectedSize, selectedSunscreen, onSuccess, statusMessage, setStatusMessage, stripePublishableKey, parentPricing }) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [clientSecret, setClientSecret] = React.useState('');
  const [pricing, setPricing] = React.useState({ subtotal: 0, tax: 0, total: 0 });
  
  // Get pricing info from parent or calculate
  React.useEffect(() => {
    const sizeOption = SIZE_OPTIONS.find(s => s.id === selectedSize);
    if (sizeOption) {
      // Calculate pricing (this should match your backend calculation)
      const subtotal = sizeOption.price * 100; // Convert to cents
      const tax = Math.round(subtotal * 0.085); // 8.5% CA tax
      const total = subtotal + tax;
      setPricing({ subtotal, tax, total });
    }
  }, [selectedSize]);
  
  // Payment element options
  const paymentElementOptions = {
    layout: 'tabs',
    defaultValues: {
      billingDetails: {
        address: {
          country: 'US'
        }
      }
    }
  };
  
  // Handle payment confirmation
  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!stripe || !elements || isProcessing) {
      console.log('Cannot submit - missing stripe/elements or already processing');
      return;
    }
    
    // Check if payment element has errors
    const { error: submitError } = await elements.submit();
    if (submitError) {
      console.error('Payment element validation error:', submitError);
      setStatusMessage(submitError.message || 'Please complete payment details');
      setIsProcessing(false);
      return;
    }
    
    setIsProcessing(true);
    setStatusMessage('Checking availability...');
    
    try {
      // Calculate number of dispenses based on size selection
      let dispenses;
      
      console.log('Calculating dispenses for:', {
        selectedSize,
        pricing,
        parentPricing,
        customPrice: selectedSize === 'custom' ? (parentPricing?.subtotal || pricing.subtotal) : null
      });
      
      if (selectedSize === 'custom') {
        // For custom size, use the pricing from parent (payment intent already created)
        const customPricing = parentPricing || pricing;
        if (!customPricing || !customPricing.total || customPricing.total <= 0) {
          console.error('Invalid pricing for custom size:', customPricing);
          setStatusMessage('Invalid custom price. Please try again.');
          setIsProcessing(false);
          return;
        }
        // Use total amount (including tax) for dispense calculation
        const priceInDollars = customPricing.total / 100;
        dispenses = Math.floor(priceInDollars * 3);
        console.log('Custom dispenses calculation:', { 
          subtotal: customPricing.subtotal / 100,
          tax: customPricing.tax / 100,
          total: priceInDollars, 
          dispenses 
        });
      } else {
        // For predefined sizes, calculate dispenses based on price
        // $1 = 3 dispenses, $2 = 6 dispenses, $4 = 12 dispenses
        const sizeOption = SIZE_OPTIONS.find(s => s.id === selectedSize);
        if (!sizeOption) {
          console.error('Size option not found for:', selectedSize);
          setStatusMessage('Invalid size selection');
          setIsProcessing(false);
          return;
        }
        const priceInDollars = sizeOption.price; // price is already in dollars
        dispenses = Math.floor(priceInDollars * 3);
        console.log('Predefined size dispenses calculation:', { sizeOption, priceInDollars, dispenses });
      }
      
      // Validate dispenses
      if (!dispenses || dispenses <= 0) {
        console.error('Invalid dispenses calculated:', dispenses);
        setStatusMessage('Error calculating dispenses. Please try again.');
        setIsProcessing(false);
        return;
      }
      
      // Step 1: Check device availability with publicApproveDispense
      const publicApproveDispense = httpsCallable(functions, 'publicApproveDispense');
      console.log('Calling publicApproveDispense with:', {
        serial: deviceSerial,
        dispenses: dispenses
      });
      
      const approvalResponse = await publicApproveDispense({
        serial: deviceSerial,
        dispenses: dispenses
      });
      
      console.log('ApproveDispense response:', approvalResponse.data);
      
      if (!approvalResponse.data?.success || !approvalResponse.data?.result?.approved) {
        const reason = approvalResponse.data?.result?.reason || 'Device not available';
        const code = approvalResponse.data?.result?.code || 'UNKNOWN';
        
        // Map error codes to user-friendly messages
        let userMessage = reason;
        switch (code) {
          case 'BUSY':
            userMessage = STATUS_MESSAGES.busy;
            break;
          case 'INACTIVE':
            userMessage = STATUS_MESSAGES.inactive;
            break;
          case 'DEVICE_OFFLINE':
          case 'CONNECTION_ERROR':
            userMessage = STATUS_MESSAGES.offline;
            break;
          case 'INSUFFICIENT_INVENTORY':
            userMessage = 'Not enough sunscreen available. Please try a smaller size.';
            break;
          case 'WRONG_MODE':
            userMessage = 'This device is not accepting payments right now.';
            break;
          default:
            userMessage = STATUS_MESSAGES.error;
        }
        
        throw new Error(userMessage);
      }
      
      // Step 2: Device approved! Process payment
      setStatusMessage('Processing payment...');
      
      console.log('About to confirm payment with Stripe...');
      console.log('Elements:', elements);
      console.log('Stripe:', stripe);
      
      const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/success`,
        },
        redirect: 'if_required'
      });
      
      console.log('Stripe confirmPayment result:', { stripeError, paymentIntent });
      
      if (stripeError) {
        throw new Error(stripeError.message || 'Payment failed');
      }
      
      if (paymentIntent && paymentIntent.status === 'succeeded') {
        // Step 3: Payment successful! The Stripe webhook will handle device notification
        setStatusMessage('Payment successful! Dispensing sunscreen...');
        
        // Success! Call the parent handler
        if (onSuccess) {
          await onSuccess(paymentIntent.id);
        } else {
          // If no onSuccess handler, navigate directly
          console.log('No onSuccess handler, navigating directly to success page');
          navigate('/success', {
            state: {
              paymentIntentId: paymentIntent.id,
              amount: SIZE_OPTIONS.find(s => s.id === selectedSize)?.price,
              dispenserSerial: deviceSerial,
              sunscreenType: selectedSunscreen || 'Coral Isles SPF30'
            }
          });
        }
      }
    } catch (error) {
      console.error('Payment error:', error);
      console.error('Full error details:', {
        message: error.message,
        code: error.code,
        details: error.details,
        stack: error.stack
      });
      setStatusMessage(error.message || STATUS_MESSAGES.error);
      setIsProcessing(false);
    }
  };
  
  // Handle Google Pay confirmation
  const handleGooglePayClick = async (paymentData) => {
    if (isProcessing) return;
    
    setIsProcessing(true);
    setStatusMessage('Processing Google Pay payment...');
    
    try {
      const sizeOption = SIZE_OPTIONS.find(s => s.id === selectedSize);
      const sizeInMl = sizeOption.amount;
      
      // Step 1: Check device availability
      const publicApproveDispense = httpsCallable(functions, 'publicApproveDispense');
      const approvalResponse = await publicApproveDispense({
        serial: deviceSerial,
        sizeInMl: sizeInMl
      });
      
      if (!approvalResponse.data?.success || !approvalResponse.data?.result?.approved) {
        const reason = approvalResponse.data?.result?.reason || 'Device not available';
        const code = approvalResponse.data?.result?.code || 'UNKNOWN';
        
        let userMessage = reason;
        switch (code) {
          case 'BUSY':
            userMessage = STATUS_MESSAGES.busy;
            break;
          case 'INACTIVE':
            userMessage = STATUS_MESSAGES.inactive;
            break;
          case 'OFFLINE':
            userMessage = STATUS_MESSAGES.offline;
            break;
          default:
            userMessage = STATUS_MESSAGES.error;
        }
        
        setStatusMessage(userMessage);
        setIsProcessing(false);
        return;
      }
      
      // Step 2: Process Google Pay payment with Stripe
      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: {
            token: paymentData.paymentMethodData.tokenizationData.token
          }
        }
      });
      
      if (error) {
        console.error('Google Pay payment failed:', error);
        setStatusMessage(error.message || 'Payment failed. Please try again.');
        setIsProcessing(false);
        return;
      }
      
      if (paymentIntent.status === 'succeeded') {
        setStatusMessage('Payment successful! Dispensing sunscreen...');
        onSuccess(paymentIntent.id);
      }
      
    } catch (error) {
      console.error('Google Pay error:', error);
      setStatusMessage('Payment failed. Please try again.');
      setIsProcessing(false);
    }
  };

  // Handle Express Checkout confirmation (keeping for Apple Pay)
  const handleExpressPaymentConfirm = async (event) => {
    setIsProcessing(true);
    setStatusMessage('Checking availability...');
    
    try {
      
      // Calculate dispenses for express checkout
      let dispenses;
      if (selectedSize === 'custom' && pricing.total > 0) {
        // Use total amount (including tax) for dispense calculation
        const priceInDollars = pricing.total / 100;
        dispenses = Math.floor(priceInDollars * 3);
      } else {
        const sizeOption = SIZE_OPTIONS.find(s => s.id === selectedSize);
        if (!sizeOption) {
          throw new Error('Invalid size selection');
        }
        const priceInDollars = sizeOption.price;
        dispenses = Math.floor(priceInDollars * 3);
      }
      
      console.log('Express checkout dispenses calculation:', { selectedSize, dispenses });
      
      // Step 1: Check device availability first
      const publicApproveDispense = httpsCallable(functions, 'publicApproveDispense');
      const approvalResponse = await publicApproveDispense({
        serial: deviceSerial,
        dispenses: dispenses
      });
      
      if (!approvalResponse.data?.success || !approvalResponse.data?.result?.approved) {
        const reason = approvalResponse.data?.result?.reason || 'Device not available';
        const code = approvalResponse.data?.result?.code || 'UNKNOWN';
        
        // Map error codes to user-friendly messages
        let userMessage = reason;
        switch (code) {
          case 'BUSY':
            userMessage = STATUS_MESSAGES.busy;
            break;
          case 'INACTIVE':
            userMessage = STATUS_MESSAGES.inactive;
            break;
          case 'DEVICE_OFFLINE':
          case 'CONNECTION_ERROR':
            userMessage = STATUS_MESSAGES.offline;
            break;
          case 'INSUFFICIENT_INVENTORY':
            userMessage = 'Not enough sunscreen available. Please try a smaller size.';
            break;
          case 'WRONG_MODE':
            userMessage = 'This device is not accepting payments right now.';
            break;
          default:
            userMessage = STATUS_MESSAGES.error;
        }
        
        throw new Error(userMessage);
      }
      
      // Step 2: Device approved! Let Stripe handle the express payment
      setStatusMessage('Processing payment...');
      
      const { error } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/success`,
        },
      });
      
      if (error) {
        throw new Error(error.message);
      }
      
      // If we get here, payment is being processed
      // The return_url will handle the success flow
    } catch (error) {
      console.error('Express payment error:', error);
      
      // Enhanced error handling for Google Pay specific scenarios
      let userMessage = error.message || STATUS_MESSAGES.error;
      
      // Handle Google Pay specific errors
      if (error.code === 'payment_intent_authentication_failure') {
        userMessage = 'Payment authentication failed. Please try again or use a different payment method.';
      } else if (error.code === 'card_declined') {
        userMessage = 'Your card was declined. Please try a different payment method.';
      } else if (error.code === 'expired_card') {
        userMessage = 'Your card has expired. Please update your payment method.';
      } else if (error.code === 'insufficient_funds') {
        userMessage = 'Insufficient funds. Please try a different payment method.';
      } else if (error.code === 'processing_error') {
        userMessage = 'Payment processing error. Please try again in a moment.';
      } else if (error.type === 'validation_error') {
        userMessage = 'Payment validation error. Please check your payment details.';
      } else if (error.message && error.message.includes('Google Pay')) {
        userMessage = 'Google Pay error: ' + error.message + '. Please try again or use a different payment method.';
      } else if (error.message && error.message.includes('network')) {
        userMessage = 'Network error. Please check your connection and try again.';
      }
      
      setStatusMessage(userMessage);
      setIsProcessing(false);
      // Express checkout error is handled by throwing
    }
  };

  // Debug payment methods availability and Google Pay compatibility
  React.useEffect(() => {
    if (stripe && elements) {
      const checkPaymentMethods = async () => {
        try {
          // Comprehensive browser and Google Pay compatibility check
          const browserInfo = {
            userAgent: navigator.userAgent,
            isChrome: /Chrome/.test(navigator.userAgent) && !/Edg/.test(navigator.userAgent),
            isFirefox: /Firefox/.test(navigator.userAgent),
            isSafari: /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent),
            isEdge: /Edg/.test(navigator.userAgent),
            isOpera: /OPR/.test(navigator.userAgent) || /Opera/.test(navigator.userAgent),
            isHTTPS: window.location.protocol === 'https:',
            isLocalhost: window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1',
            domain: window.location.hostname,
            isMobile: /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent),
            isAndroid: /Android/i.test(navigator.userAgent),
            isIOS: /iPhone|iPad|iPod/i.test(navigator.userAgent)
          };

          console.log('🔍 Browser compatibility check:', browserInfo);

          // Google Pay supported browsers according to documentation:
          // Chrome, Firefox, Safari, Edge, Opera, UCWeb UC Browser
          const isGooglePaySupportedBrowser = browserInfo.isChrome || 
                                            browserInfo.isFirefox || 
                                            browserInfo.isSafari || 
                                            browserInfo.isEdge || 
                                            browserInfo.isOpera;

          const isSecureContext = browserInfo.isHTTPS || browserInfo.isLocalhost;

          console.log('💳 Google Pay requirements check:', {
            supportedBrowser: isGooglePaySupportedBrowser,
            secureContext: isSecureContext,
            canShowGooglePay: isGooglePaySupportedBrowser && isSecureContext,
            chromeVersion: browserInfo.isChrome ? navigator.userAgent.match(/Chrome\/(\d+)/)?.[1] : null,
            firefoxVersion: browserInfo.isFirefox ? navigator.userAgent.match(/Firefox\/(\d+)/)?.[1] : null
          });

          // Additional checks for mobile devices
          if (browserInfo.isMobile) {
            console.log('📱 Mobile device detected:', {
              platform: browserInfo.isAndroid ? 'Android' : browserInfo.isIOS ? 'iOS' : 'Other',
              userAgent: browserInfo.userAgent
            });
          }

          // Warn if requirements are not met
          if (!isSecureContext) {
            console.warn('⚠️ Google Pay requires HTTPS or localhost for testing');
          }

          if (!isGooglePaySupportedBrowser) {
            console.warn('⚠️ Current browser may not support Google Pay:', {
              browser: browserInfo.userAgent,
              recommended: 'Chrome, Firefox, Safari, Edge, or Opera'
            });
          }

        } catch (err) {
          console.error('❌ Error checking payment methods:', err);
        }
      };
      checkPaymentMethods();
    }
  }, [stripe, elements]);

  return (
    <form onSubmit={handleSubmit} className="payment-form">
      {/* Express Checkout Element - Shows Apple Pay, Google Pay, etc. */}
      <div className="express-checkout-container">
        <ExpressCheckoutElement 
          options={{
            buttonType: {
              applePay: 'check-out',  // Apple Pay requires hyphenated version
              googlePay: 'checkout'    // Google Pay requires non-hyphenated version
            },
            paymentMethods: {
              applePay: 'never',  // Hide Apple Pay for now
              googlePay: 'always'
            },
            layout: {
              maxColumns: 2,
              maxRows: 0,  // Set to 0 to show all payment methods without dropdown
              overflow: 'never'  // Now compatible with maxRows: 0
            }
          }}
          onConfirm={handleExpressPaymentConfirm}
          onReady={({ availablePaymentMethods }) => {
            console.log('Express Checkout ready with methods:', availablePaymentMethods);
          }}
        />
      </div>
      
      {/* Standard Payment Element */}
      <div className="payment-element-container">
        <PaymentElement options={paymentElementOptions} />
      </div>
      


      {/* Status Messages */}
      {statusMessage && (
        <div className={`status-message ${statusMessage.includes('Error') || statusMessage.includes('offline') ? 'error' : ''}`}>
          {statusMessage}
        </div>
      )}
      
      {/* Pay Button */}
      <button
        type="submit"
        disabled={!stripe || isProcessing}
        className="pay-button primary-button"
      >
        {isProcessing ? (
          <span className="processing">
            <span className="spinner"></span>
            Processing...
          </span>
        ) : (
          'Pay'
        )}
      </button>
    </form>
  );
}

// Main checkout form component
function CheckoutForm({ deviceSerial, stripePromise }) {
  const navigate = useNavigate();
  const [selectedSize, setSelectedSize] = useState('medium');
  const [selectedSunscreen, setSelectedSunscreen] = useState('Coral Isles SPF30');
  const [clientSecret, setClientSecret] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [isLoadingIntent, setIsLoadingIntent] = useState(false);
  const [stripePublishableKey, setStripePublishableKey] = useState(null);
  const [pricing, setPricing] = useState({ subtotal: 0, tax: 0, total: 0 });
  const [customPrice, setCustomPrice] = useState('10'); // Default to $10
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [showIngredientsDropdown, setShowIngredientsDropdown] = useState(false);
  const [showPricingBreakdown, setShowPricingBreakdown] = useState(false);
  
  // Hardcode ZIP code for San Francisco location
  const zipCode = '94117'; // SF ZIP code for all purchases
  
  // Define Firebase callable functions
  const paymentOperations = httpsCallable(functions, 'paymentOperations');
  
  // Fetch Stripe publishable key on mount
  React.useEffect(() => {
    const fetchStripeKey = async () => {
      try {
        const getConfigFunction = httpsCallable(functions, 'clientConfiguration');
        const configResult = await getConfigFunction({ action: 'getConfig' });
        
        if (configResult.data?.success && configResult.data?.data?.stripe?.publishableKey) {
          setStripePublishableKey(configResult.data.data.stripe.publishableKey);
          console.log('✅ Stripe publishable key loaded for Google Pay');
        } else {
          console.error('❌ Failed to get Stripe publishable key for Google Pay');
        }
      } catch (error) {
        console.error('❌ Error fetching Stripe key for Google Pay:', error);
      }
    };
    
    fetchStripeKey();
  }, []);
  
  // Create payment intent on mount with default small size
  React.useEffect(() => {
    const createInitialIntent = async () => {
      setIsLoadingIntent(true);
      try {
        const sizeOption = SIZE_OPTIONS.find(s => s.id === 'medium');
        
        // Calculate tax manually (8.5% CA sales tax)
        const CA_TAX_RATE = 0.085;
        const subtotalCents = Math.round(sizeOption.price * 100);
        const taxCents = Math.round(subtotalCents * CA_TAX_RATE);
        const totalCents = subtotalCents + taxCents;
        
        // Create payment intent with default ZIP code for initial load
        const intentResponse = await paymentOperations({
          operation: 'createPaymentIntent',
          amount: subtotalCents, // Send subtotal, backend will calculate tax
          zipCode: zipCode, // Always use SF ZIP code
          metadata: {
            serial: deviceSerial,
            dispenses: sizeOption.amount, // Use actual amount from size option
            sunscreenType: selectedSunscreen || 'Coral Isles SPF30',
            sunscreenAmount: sizeOption.amount,
            size: 'small' // Default size for initial load
          }
        });
        
        console.log('Payment intent response:', intentResponse.data);
        console.log('Full response structure:', JSON.stringify(intentResponse.data, null, 2));
        
        // Handle nested response structure from Firebase Functions
        const responseData = intentResponse.data?.data || intentResponse.data;
        console.log('Response data extracted:', responseData);
        console.log('Client secret location:', responseData?.client_secret);
        
        if (!responseData?.client_secret) {
          console.error('Payment intent creation failed - no client secret');
          console.error('Full response for debugging:', intentResponse.data);
          throw new Error(responseData?.error || 'Failed to create payment intent');
        }
        
        setClientSecret(responseData.client_secret);
        
        // Update pricing with tax information
        setPricing({
          subtotal: responseData.subtotal || responseData.amount || 0,
          tax: responseData.tax || 0,
          total: responseData.total || responseData.amount || 0
        });
        
        setStatusMessage('');
      } catch (error) {
        console.error('Error creating payment intent:', error);
        setStatusMessage(error.message || STATUS_MESSAGES.error);
      } finally {
        setIsLoadingIntent(false);
      }
    };
    
    createInitialIntent();
  }, [deviceSerial, selectedSunscreen]);
  
  // Handle size change
  const handleSizeChange = async (sizeId) => {
    // If clicking the same size, do nothing
    if (selectedSize === sizeId) return;
    
    setSelectedSize(sizeId);
    
    // Handle custom size selection
    if (sizeId === 'custom') {
      setShowCustomInput(true);
      // Set default custom price of $10 and create payment intent
      if (!customPrice) {
        setCustomPrice('10');
      }
      // Create payment intent with default $10 or current custom price
      const price = customPrice ? parseFloat(customPrice) : 10;
      await createPaymentIntentForCustom(price);
      return;
    }
    
    // Hide custom input immediately when another size is selected
    setShowCustomInput(false);
    // Clear any custom price timeout
    if (window.customPriceTimeout) {
      clearTimeout(window.customPriceTimeout);
    }
    
    const sizeOption = SIZE_OPTIONS.find(s => s.id === sizeId);
    if (!sizeOption) return;
    
    setIsLoadingIntent(true);
    setStatusMessage('');
    
    try {
      // Calculate dispenses based on price (3 dispenses per dollar)
      const calculatedDispenses = Math.floor(sizeOption.price * 3);
      
      // Create payment intent with ZIP code
    const result = await paymentOperations({
      operation: 'createPaymentIntent',
      amount: sizeOption.price * 100, // Convert to cents
      zipCode: zipCode, // Pass ZIP code for tax calculation
      dispenses: calculatedDispenses, // Add dispenses at top level
      metadata: {
        serial: deviceSerial,
        dispenserId: deviceSerial, // Keep for backward compatibility
        size: sizeId,
        units: calculatedDispenses, // Use calculated value
        dispenses: calculatedDispenses, // Use calculated value
        sunscreenType: selectedSunscreen || 'Coral Isles SPF30',
      },
    });
    
    // Handle nested response structure from Firebase Functions
    const responseData = result.data?.data || result.data;
    
    if (responseData?.client_secret) {
      setClientSecret(responseData.client_secret);
      
      // Update pricing if tax info is available
      if (responseData.subtotal && responseData.tax !== undefined && responseData.total) {
        setPricing({
          subtotal: responseData.subtotal,
          tax: responseData.tax,
          total: responseData.total
        });
      } else {
        // Fallback to simple pricing
        setPricing({
          subtotal: sizeOption.price * 100,
          tax: 0,
          total: sizeOption.price * 100
        });
      }
    }
  } catch (error) {
    console.error('Error creating payment intent:', error);
    setStatusMessage('Error preparing payment. Please try again.');
  } finally {
    setIsLoadingIntent(false);
  }
};

// Helper function to create payment intent for custom amount
const createPaymentIntentForCustom = async (price) => {
  if (isNaN(price) || price < 1) {
    setStatusMessage('Please enter a valid amount ($1 minimum)');
    return;
  }
  
  setIsLoadingIntent(true);
  setStatusMessage('');
  
  try {
    // Calculate dispenses as 3 times the price, rounded down
    const dispenses = Math.floor(price * 3);
    
    // Create payment intent with custom amount and ZIP code
    const result = await paymentOperations({
      operation: 'createPaymentIntent',
      amount: Math.round(price * 100), // Convert to cents
      zipCode: zipCode, // Pass ZIP code for tax calculation
      dispenses: dispenses, // Add dispenses calculation
      metadata: {
        serial: deviceSerial,
        dispenserId: deviceSerial, // Keep for backward compatibility
        size: 'custom',
        customAmount: price.toFixed(2),
        sunscreenType: selectedSunscreen || 'Coral Isles SPF30',
        dispenses: dispenses, // Also store in metadata
      },
    });
    
    // Handle nested response structure from Firebase Functions
    const responseData = result.data?.data || result.data;
    
    if (responseData?.client_secret) {
      setClientSecret(responseData.client_secret);
      
      // Update pricing
      if (responseData.subtotal && responseData.tax !== undefined && responseData.total) {
        setPricing({
          subtotal: responseData.subtotal,
          tax: responseData.tax,
          total: responseData.total
        });
      } else {
        setPricing({
          subtotal: Math.round(price * 100),
          tax: 0,
          total: Math.round(price * 100)
        });
      }
    }
  } catch (error) {
    console.error('Error creating payment intent:', error);
    setStatusMessage('Error preparing payment. Please try again.');
  } finally {
    setIsLoadingIntent(false);
  }
};

// Handle custom price submission
const handleCustomPriceSubmit = async () => {
  const price = parseFloat(customPrice);
  if (isNaN(price) || price < 1) {
    setStatusMessage('Please enter a valid amount ($1 minimum)');
    return;
  }
  
  await createPaymentIntentForCustom(price);
};

  // Handle successful payment
  const handlePaymentSuccess = async (paymentIntentId) => {
    try {
      const sizeOption = SIZE_OPTIONS.find(s => s.id === selectedSize);
      
      // Store payment info in Firestore for webhook processing
      try {
        // The backend webhook will handle device notification
        // We just need to show success to the user
        setStatusMessage('Success! Your sunscreen will be dispensed shortly.');
        
        // Redirect to success page after a short delay
        setTimeout(() => {
          navigate('/success', {
            state: {
              paymentIntentId: paymentIntentId,
              amount: sizeOption.price,
              dispenserSerial: deviceSerial,
              sunscreenType: 'Coral Isles SPF30'
            }
          });
        }, 2000);
      } catch (error) {
        console.error('Post-payment error:', error);
        setStatusMessage('Payment successful! Your sunscreen will be dispensed.');
        // Still redirect on error since payment was successful
        setTimeout(() => {
          navigate('/success');
        }, 3000);
      }
    } catch (error) {
      console.error('Error confirming payment with device:', error);
      setStatusMessage('Payment processed but device notification failed. Please contact support.');
    }
  };
  
  // Payment Element options
  const paymentElementOptions = {
    layout: 'tabs',
    defaultValues: {
      billingDetails: {
        // Pre-fill any known customer details here
      }
    }
  };
  
  // Express Checkout options
  const expressCheckoutOptions = {
    buttonType: {
      applePay: 'check-out',
      googlePay: 'pay'
    },
    buttonTheme: {
      applePay: 'black',
      googlePay: 'black'
    },
    buttonHeight: 48
  };
  
  // Success state
  if (paymentSuccess) {
    return (
      <div className="checkout-container success">
        <div className="success-message">
          <div className="success-icon">✓</div>
          <h2>Payment Successful!</h2>
          <p>Your sunscreen is being dispensed.</p>
          <p className="redirect-message">Redirecting to success page...</p>
        </div>
      </div>
    );
  }
  
  // Loading state - Elements provider ensures Stripe is loaded
  const isLoading = false;
  
  return (
    <div className="checkout-container">
      <h1 className="checkout-title"><span style={{ color: '#FFD93D' }}>Sun</span> Block Checkout</h1>
      
      {/* Sunscreen Display - Always visible */}
      <div className="sunscreen-info">
        <img 
          src="/coral_isles.jpg" 
          alt="Coral Isles SPF30" 
          className="sunscreen-icon"
          onClick={() => setShowIngredientsDropdown(!showIngredientsDropdown)}
          style={{ cursor: 'pointer' }}
        />
        <h2 className="sunscreen-combined">
          <span className="sunscreen-label">Sunscreen:</span> Coral Isles SPF30
        </h2>
      </div>
        
        {/* Ingredients Dropdown */}
        {showIngredientsDropdown && (
          <div 
            className="ingredients-dropdown"
            onClick={(e) => {
              // Close dropdown when clicking anywhere inside
              // Only close if it's not a scroll event on mobile
              if (e.type === 'click') {
                setShowIngredientsDropdown(false);
              }
            }}
          >
            <div className="ingredients-header">
              <img 
                src="/coral_isles_logo.jpg" 
                alt="Coral Isles Logo" 
                className="ingredients-logo"
              />
              <h3 className="ingredients-title">What's Inside? 🌊</h3>
            </div>
            
            <img 
              src="/Coral Isles Ingredients.jpg" 
              alt="Coral Isles Ingredients" 
              className="ingredients-image"
            />
            
            <div className="ingredients-features">
              <div className="feature-grid">
                <div className="feature-item">
                  <span className="feature-icon">🐠</span>
                  <span className="feature-text">Reef Friendly</span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">🚫</span>
                  <span className="feature-text">Oxybenzone Free</span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">🚫</span>
                  <span className="feature-text">Octinoxate Free</span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">🌿</span>
                  <span className="feature-text">Paraben Free</span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">🌸</span>
                  <span className="feature-text">Fragrance Free</span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">💧</span>
                  <span className="feature-text">Water Resistant (80-min)</span>
                </div>
              </div>
            </div>
          </div>
        )}

      {/* Size Selection - Always visible */}
      <div className="size-selector">
        <h3>Select Amount:</h3>
        <div className="size-buttons">
          {SIZE_OPTIONS.map(size => (
            <button
              key={size.id}
              className={`size-button ${selectedSize === size.id ? 'selected' : ''}`}
              onClick={() => handleSizeChange(size.id)}
              type="button"
              disabled={isLoadingIntent}
            >
              <div className="size-button-content">
                <span className="size-price">{size.id === 'custom' ? '💰' : size.label}</span>
                <span className="size-description">{size.description}</span>
              </div>
            </button>
          ))}
        </div>
        
        {/* Custom Price Input */}
        {showCustomInput && (
          <div className="custom-price-input">
            <div className="custom-input-wrapper">
              <span className="dollar-sign">$</span>
              <input
                type="number"
                min="1"
                step="0.01"
                placeholder="Enter amount"
                value={customPrice}
                onChange={(e) => {
                  setCustomPrice(e.target.value);
                  // Auto-update payment intent after user stops typing
                  clearTimeout(window.customPriceTimeout);
                  window.customPriceTimeout = setTimeout(() => {
                    const price = parseFloat(e.target.value);
                    if (!isNaN(price) && price >= 1) {
                      createPaymentIntentForCustom(price);
                    }
                  }, 1000); // 1 second delay
                }}
                onBlur={() => {
                  // Update payment intent when user leaves the field
                  const price = parseFloat(customPrice);
                  if (!isNaN(price) && price >= 1) {
                    createPaymentIntentForCustom(price);
                  }
                }}
                className="price-input"
                autoFocus
              />
            </div>
            <div className="custom-price-info">
              {customPrice && parseFloat(customPrice) >= 1 && (
                <span className="dispense-count">
                  {Math.floor(parseFloat(customPrice) * 3)} dispenses
                </span>
              )}
            </div>
          </div>
        )}
        
        {/* Fun message */}
        <div className="fun-message">
          <span className="sun-emoji">☀️</span>
          <span style={{ color: '#FFEF00' }}><b>Pay it forward:</b> Anything you don't use stays available for the next person</span>
          <span className="sun-emoji">☀️</span>
        </div>
      </div>



      {/* Pricing Breakdown - Show when we have pricing info */}
      {pricing.subtotal > 0 && (
        <div className="pricing-breakdown">
          <div 
            className="pricing-total-line"
            onClick={() => setShowPricingBreakdown(!showPricingBreakdown)}
            style={{ cursor: 'pointer' }}
          >
            <span>Total</span>
            <div className="total-with-arrow">
              <span>${(pricing.total / 100).toFixed(2)}</span>
              <span className={`pricing-arrow ${showPricingBreakdown ? 'expanded' : ''}`}>▼</span>
            </div>
          </div>
          
          {showPricingBreakdown && (
            <div className="pricing-details">
              <div className="pricing-line">
                <span>Subtotal</span>
                <span>${(pricing.subtotal / 100).toFixed(2)}</span>
              </div>
              {pricing.tax > 0 && (
                <div className="pricing-line">
                  <span>Tax & Fees</span>
                  <span>${(pricing.tax / 100).toFixed(2)}</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Payment Section */}
      {isLoading ? (
        <SpinningSun 
          size="large" 
          message="Preparing checkout..." 
          className="payment-loading"
        />
      ) : stripePromise ? (
        clientSecret && !(selectedSize === 'custom' && !showCustomInput) ? (
          <Elements key={clientSecret} stripe={stripePromise} options={{ clientSecret }}>
            <PaymentForm 
              deviceSerial={deviceSerial}
              selectedSize={selectedSize}
              selectedSunscreen={selectedSunscreen}
              onSuccess={handlePaymentSuccess}
              statusMessage={statusMessage}
              setStatusMessage={setStatusMessage}
              stripePublishableKey={stripePublishableKey}
              parentPricing={pricing}
            />
          </Elements>
        ) : (
          <div>
            {/* Always show Google Pay button even without payment intent */}
            <div className="express-checkout-container">
              <GooglePayButton 
                onGooglePayClick={() => {
                  // Handle Google Pay click when no payment intent exists yet
                  if (!clientSecret) {
                    setStatusMessage('Please select a size first');
                  }
                }}
                isProcessing={isLoadingIntent}
                amount={pricing.total || SIZE_OPTIONS.find(s => s.id === selectedSize)?.price * 100 || 100}
                stripePublishableKey={stripePublishableKey}
              />
            </div>
            
            {/* Show loading or placeholder for card payment */}
            {isLoadingIntent ? (
              <SpinningSun 
                size="medium" 
                message="Initializing payment..." 
                className="payment-loading"
              />
            ) : (
              <div className="payment-placeholder">
                <p>Select a size above to continue with card payment</p>
              </div>
            )}
          </div>
        )
      ) : (
        <div className="payment-placeholder">
          <p>Loading payment options...</p>
        </div>
      )}

    </div>
  );
}

function Checkout() {
  const [searchParams] = useSearchParams();
  const deviceSerial = searchParams.get('dispenser_id') || '';
  const [stripePromise, setStripePromise] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load Stripe on mount
  React.useEffect(() => {
    const loadStripeKey = async () => {
      console.log('Starting to load Stripe key...');
      try {
        // Fetch Stripe publishable key from backend using clientConfiguration
        const getConfigFunction = httpsCallable(functions, 'clientConfiguration');
        console.log('Calling clientConfiguration...');
        const configResult = await getConfigFunction({ action: 'getConfig' });
        console.log('Config result:', configResult);
        
        if (!configResult.data?.success || !configResult.data?.data?.stripe?.publishableKey) {
          console.error('Invalid config result:', configResult.data);
          throw new Error('Failed to get Stripe configuration');
        }
        
        console.log('Loading Stripe with key:', configResult.data.data.stripe.publishableKey.substring(0, 10) + '...');
        const stripe = await loadStripe(configResult.data.data.stripe.publishableKey);
        setStripePromise(stripe);
        console.log('Stripe loaded successfully');
      } catch (err) {
        console.error('Error loading Stripe:', err);
        setError('Unable to load payment system. Please try again.');
      } finally {
        console.log('Setting loading to false');
        setLoading(false);
      }
    };

    loadStripeKey();
  }, []);

  // Loading state
  if (loading) {
    return (
      <div className="checkout-page">
        <SpinningSun 
          size="large" 
          message="Loading checkout..." 
          fullscreen={true}
        />
      </div>
    );
  }

  // Error state
  if (error || !stripePromise) {
    return (
      <div className="checkout-page">
        <div className="checkout-error">
          <h2>Error</h2>
          <p>{error || 'Unable to load checkout'}</p>
        </div>
      </div>
    );
  }

  // Main checkout with Stripe Elements
  return (
    <div className="checkout-page">
      <CheckoutForm deviceSerial={deviceSerial} stripePromise={stripePromise} />
    </div>
  );
}

export default Checkout;
